import { useCallback } from 'react'

import useDialog from '@shared/hooks/src/useDialog'

import { useStatusChangeConfig } from '@hooks/useStatusChangeConfig'

import ConfirmStatusChange from './ConfirmStatusChange'

/**
 * Single entry point for confirming a task status change across the kanban.
 *
 * @param provider - true when the actor is a provider expiring their own task, see useStatusChangeConfig.
 *
 * Returns `request(task, targetStatus, { onConfirm, onCancel })`:
 *   - if the target needs no confirmation, `onConfirm()` runs immediately
 *   - otherwise the matching dialog is shown and `onConfirm(reason)` runs on confirm
 */
export function useStatusChangeConfirmation({ provider = false } = {}) {
  const getConfig = useStatusChangeConfig({ provider })

  const open = useDialog({
    component: ConfirmStatusChange,
    props: ({ item, close }) => ({
      ...item.config,
      onCancel: () => {
        item.onCancel?.()
        close()
      },
      onConfirm: (reason) => {
        item.onConfirm?.(reason)
        close()
      },
    }),
  })

  return useCallback(
    (task, targetStatus, { onConfirm, onCancel } = {}) => {
      const config = getConfig(task, targetStatus)
      if (!config) {
        onConfirm?.()
        return
      }
      open({ config, onConfirm, onCancel })
    },
    [getConfig, open]
  )
}
